import {
  fromUserScaledNumberIncludingDelta,
  toUserScaledNumber,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import ReadingType from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import Alert from '../Alert';
import TriggeredAlertEvent from '../TriggeredAlertEvent';
import AlertAction from '../AlertAction';
import TriggerCondition from '../TriggerCondition';
import ClearCondition from '../ClearCondition';
import LastReading from '../LastReading';
import CustomTemplate from '../CustomTemplate';
import {
  defaultClearEmailBody,
  defaultClearEmailSubject,
  defaultClearTextMessage,
  defaultTriggerEmailBody,
  defaultTriggerEmailSubject,
  defaultTriggerTextMessage,
} from '../../utility/AlertNotificationTemplateUtils';
import RaaChannelKey from '../RaaChannelKey';
import { getOperatorTypeFromSymbol } from '../../components/xlalerts/alertforms/AlertFormUtility';
import NotificationAction from '../NotificationAction';

const RaaAlertMapper = {
  mapJsonToAlert: (alertJson) => {
    const alertActionList = alertJson.alertActionList.map((alertAction) => new AlertAction(
      alertAction.id,
      alertAction.type,
      alertAction.metaData,
      alertAction.isPrimaryAction,
      alertAction.reminderInterval,
      alertAction.maxReminders,
      alertAction.quietTimeList,
      alertAction.delay,
      alertAction.notificationGroupIdList || [],
      alertAction.hasReminder || false,
      alertAction.templateId || null,
    ));
    return new Alert(
      alertJson.id,
      alertJson.description,
      new TriggeredAlertEvent(
        alertJson.triggeredAlertEvent.duration,
        alertJson.triggeredAlertEvent.triggerTimestamp,
        alertJson.triggeredAlertEvent.acknowledgementTimestamp,
        alertJson.triggeredAlertEvent.acknowledgementUser,
      ),
      alertJson.timezone,
      alertJson.lastReading,
      new TriggerCondition(
        alertJson.triggerCondition.value,
        alertJson.triggerCondition.operator,
        alertJson.triggerCondition.formattedCondition,
        alertJson.triggerCondition.formattedValue,
      ),
      new ClearCondition(
        alertJson.clearCondition.value,
        alertJson.clearCondition.operator,
        alertJson.clearCondition.formattedCondition,
        alertJson.clearCondition.formattedValue,
      ),
      alertJson.temperatureScale,
      alertActionList,
      alertJson.isDeviceAssignmentSuspended,
      alertJson.isAlertDeactivated,
      alertJson.isAcknowledged,
      alertJson.isFavoritedBySessionUser,
      alertJson.channelKey,
      RaaAlertMapper.mapCustomTemplateJsonToCustomTemplate(alertJson.customTemplateList),
      alertJson.channel.unit,
      alertJson.additionalChannelKeys.map((channelKey) => new RaaChannelKey(channelKey)),
    );
  },

  mapJsonToQuickAlerts: (quickAlertsJson, initialNotificationGroupId) => {
    const quickAlerts = [];
    quickAlertsJson.forEach((alertJson, index) => {
      const mappedQuickAlert = RaaAlertMapper.getEmptyAlert('C');
      // temporary id for editing quickAlert before submit
      mappedQuickAlert.id = index + 1;
      mappedQuickAlert.description = alertJson.name;
      mappedQuickAlert.channelKey = new RaaChannelKey(alertJson.channelKey);
      mappedQuickAlert.triggerCondition.value = toUserScaledNumber(
        alertJson.threshold.value,
        mappedQuickAlert.channelKey.channelType,
        alertJson.temperatureScale,
      );
      mappedQuickAlert.triggerCondition.operator = alertJson.threshold.operator;
      mappedQuickAlert.temperatureScale = alertJson.temperatureScale;
      mappedQuickAlert.alertActionList.push(
        new NotificationAction(
          crypto.randomUUID(),
          [initialNotificationGroupId],
          null,
          false,
          null,
          null,
          null,
          null,
          false,
        ),
      );
      quickAlerts.push(mappedQuickAlert);
    });
    return quickAlerts;
  },

  getEmptyAlert: (temperatureScale) => new Alert(
    null,
    '',
    new TriggeredAlertEvent(
      null,
      null,
      null,
      null,
    ),
    '',
    new LastReading('', ''),
    new TriggerCondition(0, '', '', ''),
    new ClearCondition(0, '', '', ''),
    temperatureScale,
    [],
    false,
    false,
    false,
    false,
    'XX:XX:XX:XX:XX:XX_XX:XX:XX:XX:XX:XX_0_0_0_0',
    {},
    '',
    [],
    [],
  ),

  mapCustomTemplateJsonToCustomTemplate: (customTemplateList) => {
    const customTemplate = new CustomTemplate(
      0,
      defaultTriggerEmailSubject,
      defaultTriggerEmailBody,
      defaultClearEmailSubject,
      defaultClearEmailBody,
      0,
      defaultTriggerTextMessage,
      defaultClearTextMessage,
    );
    customTemplateList.forEach((template) => {
      const { actionType, alertState, section } = template;
      switch (actionType) {
        case 'email':
          customTemplate.emailTemplateEnabled = 1;
          if (alertState === 'trigger') {
            if (section === 'subject') {
              customTemplate.emailTemplateTriggeredSubject = template.text;
            }
            if (section === 'body') {
              customTemplate.emailTemplateTriggeredBody = template.text;
            }
          }
          if (alertState === 'clear') {
            if (section === 'subject') {
              customTemplate.emailTemplateClearedSubject = template.text;
            }
            if (section === 'body') {
              customTemplate.emailTemplateClearedBody = template.text;
            }
          }
          break;
        case 'sms':
          customTemplate.smsTemplateEnabled = 1;
          if (alertState === 'trigger') {
            customTemplate.smsTemplateTriggeredBody = template.text;
          }
          if (alertState === 'clear') {
            customTemplate.smsTemplateClearedBody = template.text;
          }
          break;
        default:
          break;
      }
    });
    return customTemplate;
  },
  mapAlertCustomTemplateToJson: (alert) => ({
    timezone: alert.timezone,
    'email-template-enabled': alert.customTemplate.emailTemplateEnabled,
    'email-template-triggered-subject': alert.customTemplate.emailTemplateTriggeredSubject,
    'email-template-triggered-body': alert.customTemplate.emailTemplateTriggeredBody,
    'email-template-cleared-subject': alert.customTemplate.emailTemplateClearedSubject,
    'email-template-cleared-body': alert.customTemplate.emailTemplateClearedBody,
    'sms-template-enabled': alert.customTemplate.smsTemplateEnabled,
    'sms-template-triggered-body': alert.customTemplate.smsTemplateTriggeredBody,
    'sms-template-cleared-body': alert.customTemplate.smsTemplateClearedBody,
    contextResourceList: alert.additionalChannelKeys
      .map((channelKey) => ({
        ...channelKey,
        channelKey: channelKey.fullChannelKey,
      })),
    alertId: alert.id,
  }),
  mapAlertToJson: (alert) => ({
    alertId: alert.id,
    temperatureScale: alert.temperatureScale,
    description: alert.description,
    contextResourceList: alert.additionalChannelKeys
      .map((channelKey) => ({ channelKey: channelKey.fullChannelKey })),
    startThresholdOperator: alert.triggerCondition.operator,
    // eslint-disable-next-line max-len
    startThresholdValue: fromUserScaledNumberIncludingDelta(alert.triggerCondition.value, alert.channelKey.channelType, alert.temperatureScale, (alert.channelKey.channelGroupType === 1500 && alert.channelKey.channelIndex === 1)),
    channelKey: alert.channelKey.fullChannelKey,
    tagIds: alert.tags.map((tag) => tag.id),
  }),
  mapNewQuickAlertToJson: (alert, temperatureScale) => ({
    alertNamePairs: [{ alertName: alert.description, channelKey: alert.channelKey.fullChannelKey }],
    thresholdValue: fromUserScaledNumberIncludingDelta(alert.triggerCondition.value,
      alert.channelKey.channelType,
      temperatureScale,
      (alert.channelKey.channelGroupType === 1500 && alert.channelKey.channelIndex === 1)),
    thresholdOperator: getOperatorTypeFromSymbol(alert.triggerCondition.operator),
    useReusableThreshold: false,
    thresholdId: 0,
    channelTypeId: alert.channelKey.channelType,
    readingTypeId: alert.channelKey.channelType === 1
      ? ReadingType.TEMPERATURE.id : ReadingType.GENERIC_BOOLEAN.id,
    actionList: alert.alertActionList.map((alertAction) => ({
      notificationGroupIdList: alertAction.notificationGroupIdList,
      delay: alertAction.delay,
      repeatEnabled: alertAction.repeatEnabled,
      repeatInterval: alertAction.repeatInterval,
      maxReminders: alertAction.maxReminders,
      quietTimeId: alertAction.quietTimeId,
      templateId: alertAction.templateId,
    })),
    tagIdList: alert.tags.map((tag) => tag.id),
    additionalChannelKeyList: [],
    timeZoneName: alert.timezone ? alert.timezone : 'UTC',
  }),
  mapNewAlertToJson: (alert) => ({
    alertId: alert.id,
    temperatureScale: alert.temperatureScale,
    description: alert.description,
    contextResourceList: alert.additionalChannelKeys
      .map((channelKey) => ({ channelKey: channelKey.fullChannelKey })),
    startThresholdOperator: alert.triggerCondition.operator,
    // eslint-disable-next-line max-len
    startThresholdValue: fromUserScaledNumberIncludingDelta(alert.triggerCondition.value, alert.channelKey.channelType, alert.temperatureScale, (alert.channelKey.channelGroupType === 1500 && alert.channelKey.channelIndex === 1)),
    channelKey: alert.channelKey.fullChannelKey,
    actionList: alert.alertActionList
      .map((alertAction) => ({
        emailAddress: alertAction.metaData.emailAddress,
        number: alertAction.metaData.number,
        countryCode: alertAction.metaData.countryCode,
        provider: alertAction.metaData.provider,
        url: alertAction.metaData.url,
        userId: alertAction.metaData.userId,
        reminderInterval: alertAction.reminderInterval ? alertAction.reminderInterval : undefined,
        maxReminders: alertAction.maxReminders ? alertAction.maxReminders : undefined,
        quietTimeList: alertAction.quietTimeList,
        delay: alertAction.delay ? alertAction.delay : undefined,
        type: alertAction.type,
      })),
    tagIds: alert.tags.map((tag) => tag.id),
  }),
  mapAlertActionToJson: (alertAction, alertId) => ({
    emailAddress: alertAction.metaData.emailAddress,
    number: alertAction.metaData.number,
    countryCode: alertAction.metaData.countryCode,
    provider: alertAction.metaData.provider,
    url: alertAction.metaData.url,
    userId: alertAction.metaData.userId,
    hasReminder: alertAction.hasReminder ? 'on' : undefined,
    reminderInterval: alertAction.reminderInterval,
    hasMaxReminders: alertAction.hasMaxReminders ? 'on' : undefined,
    maxReminders: alertAction.maxReminders,
    hasQuietTimes: alertAction.hasQuietTimes ? 'on' : undefined,
    quietTimeList: alertAction.quietTimeList,
    hasDelay: alertAction.hasDelay ? 'on' : undefined,
    delay: alertAction.delay,
    type: alertAction.type,
    alertActionId: alertAction.id,
    action: alertAction.id ? 'modify' : 'create',
    alertId,
  }),
};

export default RaaAlertMapper;
